/**
 * Auth state for the dashboard, over whichever provider the edge reports.
 *
 * The component tree only ever sees `status`, `identity` and `signIn`; which
 * provider is behind them is decided by GET /api/auth/config at boot. That is
 * what lets the same bundle run against a Clerk tenant or the local dev
 * provider without a rebuild.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import {
  getAuthConfig,
  devLogin,
  setTokenGetter,
  userFacingError,
} from '@/api/client';
import type { AuthConfig } from '@/api/types';
import { ensureActiveOrg, loadClerk, type ClerkGlobal } from './clerk';

const DEV_TOKEN_KEY = 'showrunner.devToken.v1';

export type AuthStatus =
  | 'loading'
  | 'signed-out'
  | 'signed-in'
  | 'error';

export interface Identity {
  userId: string;
  email: string;
}

interface AuthState {
  status: AuthStatus;
  identity: Identity | null;
  config: AuthConfig | null;
  error: string | null;
  signIn: (input?: { email?: string; org?: string }) => Promise<void>;
  signOut: () => Promise<void>;
  retry: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [identity, setIdentity] = useState<Identity | null>(null);
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const clerkRef = useRef<ClerkGlobal | null>(null);

  const retry = useCallback(() => {
    setError(null);
    setStatus('loading');
    setAttempt((n) => n + 1);
  }, []);

  // Which boot run is current. Settles compare against this instead of a
  // closure-scoped `cancelled`: in production we observed settles completing
  // and then finding themselves cancelled even though no cleanup had logged —
  // the status flip silently dropped and the gate spun forever. With a run
  // id, teardown of THIS run can never orphan the app; only a newer run
  // supersedes, and the newest run always flips status.
  const bootRunRef = useRef(0);

  useEffect(() => {
    const runId = bootRunRef.current + 1;
    bootRunRef.current = runId;
    const isCurrent = () => bootRunRef.current === runId;

    async function settleSignedIn(clerk: ClerkGlobal): Promise<void> {
      try {
        // Bounded: a hang here used to strand the user on "Checking how to
        // sign you in…" with no error and no way out.
        await Promise.race([
          ensureActiveOrg(clerk),
          new Promise((_, rej) =>
            window.setTimeout(
              () => rej(new Error('timed out — try again')),
              20_000,
            ),
          ),
        ]);
      } catch (err) {
        // Better a named failure than the silent 401 loop this replaced: a
        // user who cannot get an org cannot use the app, and should be told.
        if (isCurrent()) {
          setError(
            userFacingError(
              err,
              'We couldn’t finish setting up your workspace. Please try again.',
            ),
          );
          setStatus('error');
        }
        return;
      }
      if (!isCurrent()) {
        return;
      }
      setIdentity(clerk.user ? toIdentity(clerk.user) : null);
      setStatus(clerk.user ? 'signed-in' : 'signed-out');
    }

    async function boot(): Promise<void> {
      try {
        const cfg = await getAuthConfig();
        if (!isCurrent()) return;
        setConfig(cfg);

        if (cfg.provider === 'dev') {
          setTokenGetter(async () => localStorage.getItem(DEV_TOKEN_KEY));
          const existing = localStorage.getItem(DEV_TOKEN_KEY);
          if (existing) {
            setIdentity(readDevIdentity(existing));
            setStatus('signed-in');
          } else {
            setStatus('signed-out');
          }
          return;
        }

        const clerk = await loadClerk(
          cfg.publishable_key ?? '',
          cfg.script_url ?? '',
        );
        if (!isCurrent()) return;
        await clerk.load();
        if (!isCurrent()) return;
        clerkRef.current = clerk;
        setTokenGetter(async () => (await clerk.session?.getToken()) ?? null);

        clerk.addListener?.(({ user }) => {
          if (!isCurrent()) return;
          if (user) {
            // A sign-in that just completed may have no active org yet; gate
            // 'signed-in' on provisioning one, or the first API call 401s.
            void settleSignedIn(clerk);
          } else {
            setIdentity(null);
            setStatus('signed-out');
          }
        });

        if (clerk.user) {
          await settleSignedIn(clerk);
        } else {
          setIdentity(null);
          setStatus('signed-out');
        }
      } catch (err) {
        if (!isCurrent()) return;
        // Surface the failure instead of spinning forever. The previous
        // implementation retried every 200ms with no timeout and no message.
        setError(
          userFacingError(
            err,
            'Sign-in is temporarily unavailable. Please try again.',
          ),
        );
        setStatus('error');
      }
    }

    void boot();
    // Deliberately no invalidation on cleanup: the provider is the app root
    // and a torn-down run is harmless, while an invalidated run that was the
    // ONLY run left the gate spinning forever. A retry bumps the run id.
    return undefined;
  }, [attempt]);


  const signIn = useCallback(
    async (input?: { email?: string; org?: string }) => {
      if (config?.provider === 'dev') {
        const { token, email } = await devLogin(
          input?.email?.trim() || 'dev@localhost',
          input?.org?.trim() || 'local-dev',
        );
        localStorage.setItem(DEV_TOKEN_KEY, token);
        setIdentity(readDevIdentity(token) ?? { userId: 'dev', email });
        setStatus('signed-in');
        return;
      }
      clerkRef.current?.openSignIn({ afterSignInUrl: window.location.href });
    },
    [config],
  );

  const signOut = useCallback(async () => {
    if (config?.provider === 'dev') {
      localStorage.removeItem(DEV_TOKEN_KEY);
      setIdentity(null);
      setStatus('signed-out');
      return;
    }
    await clerkRef.current?.signOut();
    setIdentity(null);
    setStatus('signed-out');
  }, [config]);

  const value = useMemo<AuthState>(
    () => ({ status, identity, config, error, signIn, signOut, retry }),
    [status, identity, config, error, signIn, signOut, retry],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function toIdentity(user: { id: string; primaryEmailAddress?: { emailAddress?: string } | null }): Identity {
  return {
    userId: user.id,
    email: user.primaryEmailAddress?.emailAddress ?? user.id,
  };
}

/** Read the unverified claims out of a dev token, for display only. */
function readDevIdentity(token: string): Identity | null {
  try {
    const [body] = token.split('.');
    if (!body) return null;
    const padded = body + '='.repeat((-body.length % 4 + 4) % 4);
    const json = JSON.parse(
      atob(padded.replace(/-/g, '+').replace(/_/g, '/')),
    ) as { uid?: string; email?: string };
    if (!json.uid || !json.email) return null;
    return { userId: json.uid, email: json.email };
  } catch {
    return null;
  }
}
